import { ProjectInfoCard, useCommonServiceClientContext } from '@geneo2-web/shared-ui';
import { SxProps } from '@mui/material';
import { AssessmentTask } from '@protos/learning_management/lms.assessment.common.apis_pb';
import { ProjectGroupMarkSummary } from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import { useEffect, useRef, useState } from 'react';

interface IProps {
  project: AssessmentTask;
  onClick?: () => void;
  rootStyles?: SxProps;
}

/**
 * Wraps the presentational `ProjectInfoCard` and lazily loads the project's real
 * collaboration groups (with their members) when the details popup is opened.
 * A project with two groups shows both groups and each group's members.
 *
 * The loaded data is tracked against its assessment id, so if React reuses this
 * instance for a different card the stale groups are dropped and refetched.
 */
export const ProjectInfoCardWithGroups = ({
  project,
  onClick,
  rootStyles,
}: IProps) => {
  const { LmsAssessmentTeacherAPIServiceV2WithStatusCodeHandler } =
    useCommonServiceClientContext();

  const assessmentId = project?.assessmentId ? project.assessmentId.toString() : undefined;

  const [groups, setGroups] = useState<ProjectGroupMarkSummary[]>();
  const [loading, setLoading] = useState(false);
  /** Assessment id the current `groups` belong to (null once, or on a mismatch). */
  const loadedForRef = useRef<string | null>(null);
  const inFlightRef = useRef(false);
  /** Latest assessment id, to discard responses that arrive after the card is reused. */
  const currentIdRef = useRef(assessmentId);
  currentIdRef.current = assessmentId;

  // Drop stale data if this instance gets reused for a different project.
  useEffect(() => {
    if (loadedForRef.current !== null && loadedForRef.current !== assessmentId) {
      loadedForRef.current = null;
      inFlightRef.current = false;
      setGroups(undefined);
      setLoading(false);
    }
  }, [assessmentId]);

  const loadGroupDetails = async () => {
    if (!assessmentId) return;
    if (inFlightRef.current) return;
    if (loadedForRef.current === assessmentId) return; // already loaded for this card

    inFlightRef.current = true;
    setLoading(true);
    try {
      const response =
        await LmsAssessmentTeacherAPIServiceV2WithStatusCodeHandler.getProjectMarks({
          assessmentId: BigInt(assessmentId),
        });
      // Ignore a response that arrived after the card was reused for another project.
      if (currentIdRef.current === assessmentId) {
        loadedForRef.current = assessmentId;
        setGroups(response?.data?.groups ?? []);
      }
    } catch (error) {
      console.error('Failed to load project group details', error);
      setGroups([]);
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  };

  return (
    <ProjectInfoCard
      project={project}
      onClick={onClick}
      rootStyles={rootStyles}
      createdBy={project?.teacherName}
      groupDetails={groups}
      groupDetailsLoading={loading}
      onDetailsOpen={loadGroupDetails}
    />
  );
};

export default ProjectInfoCardWithGroups;
